import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { formatDate } from '../../../utils/financialCalculations';
import { formatPercent } from '../../../utils/formatting';

const ExecutiveSummary = ({ reportData }) => {
    const claimant = {
        name: reportData.claimantName || reportData.fullName || 'N/A',
    };

    const valuationDates = {
        valuationDate: formatDate(reportData.valuationDate),
        worklifeExpectancyYears: reportData.worklifeExpectancyYears || 'N/A',
        retirementAge: reportData.retirementAge !== null && reportData.retirementAge !== undefined ? reportData.retirementAge : 'N/A',
        lifeExpectancyYears: reportData.lifeExpectancyYears || 'N/A',
        lifespanAge: reportData.lifespanAge !== null && reportData.lifespanAge !== undefined ? reportData.lifespanAge : 'N/A',
    };

    const financialRates = {
        discountRate: formatPercent(reportData.discountRate),
        growthRate: formatPercent(reportData.growthRate),
        netDiscountRate: formatPercent(reportData.netDiscountRate, 2),
    };

    return (
        <>
            <Box className="report-section" sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Methodologies & Executive Summary</Typography>
                <Typography paragraph>
                    This economic assessment has been prepared utilizing the demographics of {claimant.name} to generate a representative life model. The enclosed report depicts the anticipated economic contributions provided from {formatDate(reportData.valuationDate)} to {formatDate(reportData.expectedEndOfLife)}. Results are provided in discounted present value amounts and undiscounted future value amounts.
                </Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Worklife Expectancy:</strong> {valuationDates.worklifeExpectancyYears} years</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Retirement Age:</strong> {valuationDates.retirementAge}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Life Expectancy:</strong> {valuationDates.lifeExpectancyYears} years</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Projected Lifespan (Age):</strong> {valuationDates.lifespanAge}</Typography>
                <Divider sx={{ my: 1, width: '50%' }} />
                <Typography sx={{ mb: 0.5 }}><strong>Discount Rate:</strong> {financialRates.discountRate}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Growth Rate:</strong> {financialRates.growthRate}</Typography>
                <Typography sx={{ mb: 0.5 }}><strong>Net Discount Rate:</strong> {financialRates.netDiscountRate}</Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
        </>
    );
};

ExecutiveSummary.addPdfContent = (pdf, reportData, y) => {
    const claimant = {
        name: reportData.claimantName || reportData.fullName || 'N/A',
    };

    const valuationDates = {
        valuationDate: formatDate(reportData.valuationDate),
        worklifeExpectancyYears: reportData.worklifeExpectancyYears || 'N/A',
        retirementAge: reportData.retirementAge !== null && reportData.retirementAge !== undefined ? reportData.retirementAge : 'N/A',
        lifeExpectancyYears: reportData.lifeExpectancyYears || 'N/A',
        lifespanAge: reportData.lifespanAge !== null && reportData.lifespanAge !== undefined ? reportData.lifespanAge : 'N/A',
    };

    const financialRates = {
        discountRate: formatPercent(reportData.discountRate),
        growthRate: formatPercent(reportData.growthRate),
        netDiscountRate: formatPercent(reportData.netDiscountRate, 2),
    };

    pdf.setFont('helvetica', 'bold');
    pdf.text('Methodologies & Executive Summary', 15, y);
    y += 7;
    pdf.setFont('helvetica', 'normal');
    const summaryParagraph =
        `This economic assessment has been prepared utilizing the demographics of ${claimant.name} to generate a representative life model. ` +
        `The enclosed report depicts the anticipated economic contributions provided from ${valuationDates.valuationDate} to ${formatDate(reportData.expectedEndOfLife)}. ` +
        `Results are provided in discounted present value amounts and undiscounted future value amounts.`;
    const wrappedSummary = pdf.splitTextToSize(summaryParagraph, 180);
    pdf.text(wrappedSummary, 15, y);
    y += 6 * wrappedSummary.length + 2;
    const summaryKeyLines = [
        `Worklife Expectancy: ${valuationDates.worklifeExpectancyYears} years`,
        `Retirement Age: ${valuationDates.retirementAge}`,
        `Life Expectancy: ${valuationDates.lifeExpectancyYears} years`,
        `Projected Lifespan (Age): ${valuationDates.lifespanAge}`,
        `Discount Rate: ${financialRates.discountRate}`,
        `Growth Rate: ${financialRates.growthRate}`,
        `Net Discount Rate: ${financialRates.netDiscountRate}`,
    ];
    summaryKeyLines.forEach(line => {
        const wrapped = pdf.splitTextToSize(line, 180);
        pdf.text(wrapped, 15, y);
        y += 6 * wrapped.length;
    });
    y += 2;
    pdf.setDrawColor(200);
    pdf.line(15, y, 195, y);
    y += 6;
    return y;
};

export default ExecutiveSummary;